/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { KeyboardArrowDown } from '@mui/icons-material';
import { Autocomplete, TextField } from '@mui/material';
import { FilterOptionsState } from '@mui/material/useAutocomplete';
import { makeStyles } from '@mui/styles/';
import clsx from 'clsx';
import { useFormikContext } from 'formik';
import React, { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectOverrideRecommendedAntenna } from '../../../store';
import { setOverrideRecommendedAntenna } from '../../../store/missionPlans/actions';
import CustomListBox from '../../common/CustomListBox';
import CustomPaper from '../../common/CustomPaper';
import appTheme from '../../common/theme/appTheme.json';
import { FormValues, FormikFieldEnum, FormikValueType } from './MissionDetailsForm';

export interface SelectBoxProps {
  options: string[];
  fieldName: FormikFieldEnum;
  children?: string;
  isDisabled: boolean;
  filter: (options: any[], state: FilterOptionsState<any>) => any[];
}

export const useStyles = makeStyles({
  select: {
    backgroundColor: appTheme.mptPrimaryBackgroundColor,
    fontSize: '16px',
    fontFamily: appTheme.appFontFamily,
    borderRadius: '5px',
    paddingLeft: '10px',
    WebkitBoxSizing: 'border-box',
    MozBoxSizing: 'border-box',
    boxSizing: 'border-box',
    width: '100%',
    height: '38px',
  },
  enabled: {
    color: appTheme.primaryTextColor,
    border: `1px solid ${appTheme.primaryTextColor}`,
  },
  disabled: {
    color: appTheme.disabledTextColor,
    border: `1px solid ${appTheme.disabledTextColor}`,
    '& .MuiInputBase-input.Mui-disabled': {
      color: appTheme.disabledTextColor,
      WebkitTextFillColor: appTheme.disabledTextColor, 
    },
  },
  placeholderEnabled: {
    '&::placeholder': {
      color: appTheme.primaryTextColor,
      opacity: 0.5,
    },
  },
  placeholderDisabled: {
    '&::placeholder': {
      color: appTheme.disabledTextColor,
      opacity: 0.5,
    },
  },
  autocompleteRoot: {
    '& .MuiAutocomplete-popupIndicator': {
      color: appTheme.mptSelectIconGray,
    },
    '& .MuiAutocomplete-clearIndicator': {
      color: appTheme.mptSelectIconGray,
    },
  },
  autocompleteNoOptions: { color: appTheme.primaryTextColor },
  autocompleteOptions: {
    '&:hover': {
      backgroundColor: appTheme.primaryBackgroundColor,
    },
  },
});

const SelectBox = (props: React.PropsWithChildren<SelectBoxProps>) => {
  const { options, fieldName, children, isDisabled, filter } = props;
  const classes = useStyles();
  const { values, setFieldValue } = useFormikContext<FormValues>();
  let isMarginal =
    fieldName === FormikFieldEnum.TAKEOFF_BUFFER ||
    fieldName === FormikFieldEnum.HANDOFF_BUFFER ||
    fieldName === FormikFieldEnum.LANDING_BUFFER;
  const [selectboxValue, setSelectboxValue] = useState<string>(values[fieldName].toString());
  const overrideRecommended = useSelector(selectOverrideRecommendedAntenna, shallowEqual);
  const dispatch = useDispatch();

  // Clear Antenna Type SelectBox if Tail Number isn't specified
  useEffect(() => {
    if (isDisabled && fieldName === FormikFieldEnum.ANTENNA_TYPE) {
      setFieldValue(FormikFieldEnum.ANTENNA_TYPE, '', false);
    }
  }, [isDisabled, fieldName, setFieldValue]);

  // Set antenna value for select box if Formik antenna value changed
  useFormikValueForSelectbox(FormikFieldEnum.ANTENNA_TYPE, values.antennaType);

  // Set tail number for select box if Formik tail number changed
  useFormikValueForSelectbox(FormikFieldEnum.TAIL_NUMBER, values.tailNumber);

  // Set takeoff value for select box if Formik takeoff value changed
  useFormikValueForSelectbox(FormikFieldEnum.TAKEOFF_BUFFER, values.takeoffBuffer);

  // Set handoff value for select box if Formik handoff value changed
  useFormikValueForSelectbox(FormikFieldEnum.HANDOFF_BUFFER, values.handoffBuffer);

  // Set landing value for select box if Formik landing value changed
  useFormikValueForSelectbox(FormikFieldEnum.LANDING_BUFFER, values.landingBuffer);

  function useFormikValueForSelectbox(formikField: FormikFieldEnum, value: FormikValueType) {
    useEffect(() => {
      if (fieldName === formikField && value.toString() !== selectboxValue) {
        setSelectboxValue(value.toString());
      }
    }, [value, formikField]);
  }

  const handleChange = (event: React.ChangeEvent<{}>, value: any) => {
    event.preventDefault();

    //Edge cases
    if (value === null) {
      setFieldValue(fieldName, isMarginal ? 0 : '', false);
      setSelectboxValue('');
      return;
    } else if (value === selectboxValue) {
      return;
    } else if (fieldName === FormikFieldEnum.TAIL_NUMBER && overrideRecommended) {
      dispatch(setOverrideRecommendedAntenna(false));
    }

    //Sets the value of the select box
    setSelectboxValue(value);

    //Sets the field value for Formik
    switch (fieldName) {
      case FormikFieldEnum.TAIL_NUMBER:
        setFieldValue(FormikFieldEnum.TAIL_NUMBER, value, false);
        break;
      case FormikFieldEnum.ANTENNA_TYPE:
        setFieldValue(FormikFieldEnum.ANTENNA_TYPE, value, false);
        break;
      case FormikFieldEnum.TAKEOFF_BUFFER:
        setFieldValue(FormikFieldEnum.TAKEOFF_BUFFER, value, false);
        break;
      case FormikFieldEnum.HANDOFF_BUFFER:
        setFieldValue(FormikFieldEnum.HANDOFF_BUFFER, value, false);
        break;
      case FormikFieldEnum.LANDING_BUFFER:
        setFieldValue(FormikFieldEnum.LANDING_BUFFER, value, false);
        break;
      default:
    }
  };

  //Prevent dropdowns from submitting with 'Enter' key
  const onKeyDown = (keyEvent: { key: string; preventDefault: () => void }) => {
    if (keyEvent.key === 'Enter') {
      keyEvent.preventDefault();
    }
  };

  return (
    <React.Fragment>
      <Autocomplete
        options={options}
        onKeyDown={onKeyDown}
        getOptionLabel={option => (isMarginal ? option + ' Minutes' : option.toString())}
        filterOptions={(option, state) => filter(option, state)}
        classes={{
          root: classes.autocompleteRoot,
          noOptions: classes.autocompleteNoOptions,
          option: classes.autocompleteOptions,
        }}
        value={isDisabled ? '' : selectboxValue}
        PaperComponent={CustomPaper as React.ComponentType<React.HTMLAttributes<HTMLElement>>}
        ListboxComponent={CustomListBox as React.ComponentType<React.HTMLAttributes<HTMLElement>>}
        popupIcon={<KeyboardArrowDown />}
        disabled={isDisabled}
        disableClearable={isMarginal}
        autoComplete={false}
        onChange={(event, value) => handleChange(event, value)}
        renderInput={params => (
          <TextField
            {...params}
            placeholder={children}
            fullWidth
            InputProps={{
              ...params.InputProps,
              disableUnderline: true,
              className: clsx(classes.select, {
                [classes.enabled]: !isDisabled,
                [classes.disabled]: isDisabled,
              }),
            }}
            inputProps={{
              ...params.inputProps,
              className: clsx({
                [classes.placeholderEnabled]: !isDisabled,
                [classes.placeholderDisabled]: isDisabled,
              }),
            }}
          />
        )}
      />
    </React.Fragment>
  );
};

export default SelectBox;
